import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';

import { clearPatchOrderedParts } from '../../actions/coilovers/orderedParts/patchOrderedParts';
import deliveredOrderedParts from '../../thunks/coilovers/orderedParts/deliveredOrderedParts';
import orderedPartsStatus from '../../constants/orderedPartsStatus';
import getWarehouseExist, { clearWarehouseExist } from '../../thunks/general/getWarehouseExist';
import Modal from '../Modal/Modal';
import Text from '../Text/Text';
import Badge from '../Badge/Badge';
import ButtonTypes from '../../constants/buttonTypes';
import Input from '../Input/Input';
import Alert from '../Alert/Alert';
import AlertType from '../../common/alertType';
import { IconLoading } from '../../assets/icon';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import WarehouseEditModal from '../WarehouseEditModal/WarehouseEditModal';
import warehousePartsStatus from '../../constants/warehousePartsStatus';

const DeliveredOrderedPartsModal = ({ visible, toggleVisible, item }) => {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [warehouseModalVisible, setWarehouseModalVisible] = useState(false);
  const dispatch = useDispatch();
  const {
    patchOrderedParts: {
      loading, error: patchError, data: patchedData
    },
    getWarehouseExist: {
      data: warehouseExist,
      loading: warehouseExistLoading
    }
  } = useSelector((state) => ({
    patchOrderedParts: state?.patchOrderedParts,
    getWarehouseExist: state?.getWarehouseExist
  }), shallowEqual);

  useEffect(() => {
    if (Object.keys(patchedData).length) {
      dispatch(clearPatchOrderedParts());
      toggleVisible();
    }
  }, [patchedData]);

  const {
    _id,
    ordered_part_id: partId,
    ordered_part_po: po,
    ordered_part_mpn: orderedMpn,
    ordered_part_qty_in_order: orderedQty,
    ordered_part_qty_delivered: deliveredQty,
    ordered_part_qty_return: returnQty,
    ordered_part_status: partStatus,
    ordered_part_brand: partBrand,
    ordered_part_type: partType,
    ordered_part_price_paid: paidPrice
  } = item;

  const {
    values: {
      partOrderedQty,
      partDeliveredQty,
      partDeliveredNowQty,
      partReturnQty,
    },
    handleSubmit,
    setValues,
    isValid,
    handleBlur,
    handleChange,
    touched,
    errors
  } = useFormik({
    initialValues: {
      partOrderedQty: 0,
      partDeliveredQty: 0,
      partDeliveredNowQty: 0,
      partReturnQty: 0,
    },
    onSubmit: (values) => {
      dispatch(deliveredOrderedParts({
        _id,
        formData: {
          ordered_part_qty_delivered: parseInt(values?.partDeliveredNowQty),
          ordered_part_qty_return: parseInt(values?.partReturnQty),
          ordered_part_mpn: orderedMpn,
          ordered_part_status: (partDeliveredQty + partReturnQty + partDeliveredNowQty) >= partOrderedQty
            ? orderedPartsStatus.DELIVERED : orderedPartsStatus.PARTIALLY
        },
      }));
    },
    validationSchema: Yup.object({
      partOrderedQty: Yup.number().required('Required'),
      partDeliveredQty: Yup.number().required('Required'),
      partReturnQty: Yup.number().required('Required'),
      partDeliveredNowQty: Yup.number().required('Required'),
    })
  });

  useEffect(() => {
    setValues({
      partOrderedQty: parseInt(orderedQty),
      partDeliveredQty: parseInt(deliveredQty) || 0,
      partReturnQty: parseInt(returnQty) || 0,
      partDeliveredNowQty: parseInt(orderedQty) - (parseInt(deliveredQty) || 0) - (parseInt(returnQty) || 0),
    }, true);
    dispatch(getWarehouseExist(orderedMpn));
  }, [item]);

  return (
    <>
      <Modal
        visible={visible}
        toggleVisible={() => {
          toggleVisible();
          batch(() => {
            dispatch(clearPatchOrderedParts());
            dispatch(clearWarehouseExist());
          });
        }}
        title="Delivered part"
        subTitle={`id: ${partId} | po: ${po} | npm: ${orderedMpn}`}
        onSave={() => setConfirmVisible(true)}
        canSave={isValid && loading}
      >
        <div className="customScroll w-[500px] px-[10px]">
          <div className="flex flex-row">
            <div className="flex flex-row">
              <Text text="Current status:  " muted styleContainer="mr-[8px]" />
              <Text text={` ${partStatus}`} bold />
            </div>
            <span className="mr-[20px]" />
            <div className="flex flex-row">
              <Text text="Next status:  " muted styleContainer="mr-[8px]" />
              <Text
                text={(partDeliveredQty + partReturnQty + partDeliveredNowQty) >= partOrderedQty
                  ? orderedPartsStatus.DELIVERED : orderedPartsStatus.PARTIALLY}
                bold
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex flex-row">
              <Text text="Current:  " muted styleContainer="mr-[8px]" />
              <Badge
                text={`${(parseInt(deliveredQty) || 0) + (parseInt(returnQty) || 0)}/${orderedQty}`}
                type={ButtonTypes.LIGHT_SUCCESS}
              />
            </div>
          </div>
          <br />
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Delivered:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                type="number"
                name="partDeliveredNowQty"
                value={`${partDeliveredNowQty}`}
                placeholder="Now delivered"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partDeliveredNowQty && errors?.partDeliveredNowQty}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Return:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                type="number"
                name="partReturnQty"
                value={`${partReturnQty}`}
                placeholder="Part return qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partReturnQty && errors?.partReturnQty}
              />
            </div>
          </div>
          {patchError && <Alert text={patchError} type={AlertType.warning} />}
          <br />
          {warehouseExistLoading && (
          <div className="flex justify-center p-[5px]">
            <IconLoading className="spin" />
          </div>
          )}
          {!warehouseExistLoading && !warehouseExist.length && (
            <Alert
              text={`Mpn: ${orderedMpn} not exist`}
              type={AlertType.info}
              onAction={() => setWarehouseModalVisible(true)}
              actionText="Create?"
            />
          )}
          {!paidPrice && (
            <Alert
              text="Price paid not exist"
              type={AlertType.important}
            />
          )}
        </div>
      </Modal>
      <ConfirmModal
        onConfirm={() => {
          setConfirmVisible(false);
          handleSubmit();
        }}
        visible={confirmVisible}
        onCancel={() => {
          setConfirmVisible(false);
        }}
      />
      <WarehouseEditModal
        visible={warehouseModalVisible}
        toggleVisible={() => setWarehouseModalVisible(false)}
        item={{
          part_whs_mpn: orderedMpn,
          part_whs_brand: partBrand,
          part_whs_type: partType,
          part_whs_status: warehousePartsStatus.OUT_OF_STOCK
        }}
      />
    </>
  );
};

DeliveredOrderedPartsModal.propTypes = {
  toggleVisible: PropTypes.func,
  visible: PropTypes.bool,
  item: PropTypes.shape({
    _id: PropTypes.string,
    ordered_part_id: PropTypes.number,
    ordered_part_po: PropTypes.string,
    ordered_part_status: PropTypes.string,
    ordered_part_type: PropTypes.string,
    ordered_part_vendor: PropTypes.string,
    ordered_part_brand: PropTypes.string,
    ordered_part_mpn: PropTypes.string,
    ordered_part_qty_in_order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_qty_delivered: PropTypes.number,
    ordered_part_qty_return: PropTypes.number,
    ordered_part_tracking: PropTypes.string,
    ordered_part_price_paid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_note: PropTypes.string,
  })
};

DeliveredOrderedPartsModal.defaultProps = {
  toggleVisible: () => {},
  visible: false,
  item: {}
};

export default DeliveredOrderedPartsModal;
